/**
 * FAQ content — single source of truth.
 *
 * Consumed by BOTH page.tsx (FAQPage JSON-LD for search) and
 * FAQsContent.tsx (the visible accordion). Keeping them in one file
 * guarantees Google never shows an answer the page doesn't.
 *
 * July 2026 rewrite: the old answers claimed things that aren't true
 * ("verified by environmental scientists", "end-to-end encryption",
 * "regular security audits", "cross-referencing with lab results").
 * Every claim below is verifiable against the app or the policy pages.
 */

export interface FAQItem {
  question: string
  /** Paragraphs. Rendered as prose; joined with spaces for JSON-LD. */
  answer: string[]
  category: string
}

export const FAQ_CATEGORIES = [
  'Microplastics',
  'The app',
  'Scoring',
  'Pricing',
  'Privacy',
] as const

export const FAQS: FAQItem[] = [
  {
    category: 'Microplastics',
    question: 'What are microplastics and why do they matter?',
    answer: [
      'Microplastics are plastic particles under 5 millimeters; nanoplastics are far smaller and can pass into cells. They come from two sources: particles manufactured small, like the microbeads once common in cosmetics, and particles shed as larger plastic breaks down through heat, wear, and washing.',
      'They have been detected in drinking water, food, indoor air, and in human blood, placenta, lung tissue, and arterial plaque. The health research is early but moving quickly: a 2024 New England Journal of Medicine study linked microplastics in arterial plaque to a 4.53 times higher rate of cardiovascular events.',
      'The practical point is that most personal exposure comes from a small set of everyday routines, which means it is measurable and reducible.',
    ],
  },
  {
    category: 'The app',
    question: 'How does the MicroPlastics app work?',
    answer: [
      'Three parts. First, the scan: point the camera at a barcode or the packaging itself, and the app identifies the product, its packaging material, and how it is likely used.',
      'Second, the score: the product gets a 0 to 100 exposure signal built from published, peer-reviewed research, with the citations shown in the result.',
      'Third, the profile: saved scans build a picture of your weekly exposure by room, routine, and category, and the app suggests the swap that would move your number the most. A scan-aware AI Exposure Coach is there for follow-up questions, with the sources it drew from.',
    ],
  },
  {
    category: 'Scoring',
    question: 'How accurate is the score? Is it a lab measurement?',
    answer: [
      'No. Counting the particles in your specific bottle would take an electron microscope, and no app can do that. The score is a research-based estimate of relative risk: it weighs the packaging material, heat and reuse conditions, and category-level findings from published studies.',
      'Each result shows a confidence level and a likely range, and links the studies behind it so you can check the reasoning yourself. Treat it as decision support for choosing between products, not as a diagnosis or a lab test.',
    ],
  },
  {
    category: 'The app',
    question: 'What can I scan?',
    answer: [
      'Anything with a barcode or a packaging photo: bottled water and drinks, packaged food, coffee and tea, cosmetics and skincare, baby products, food containers, and household items.',
      'The camera has five modes (Auto, Barcode, Label, Ingredients, Material), and you can type a product name when there is nothing to photograph. The scoring model adapts to the category: a water bottle is scored on different factors than a face cream.',
    ],
  },
  {
    category: 'Pricing',
    question: 'Is the app free?',
    answer: [
      'Yes to start: 3 complete scans, no account and no card required.',
      'Premium is $7.99 per month or $49.99 per year with a 3-day free trial, and unlocks unlimited scans, detailed reports, and exposure tracking. Subscriptions are billed through your Apple ID and can be canceled anytime in App Store settings.',
    ],
  },
  {
    category: 'Microplastics',
    question: 'How can I reduce my microplastic exposure?',
    answer: [
      'The highest-leverage changes are consistent across studies: filter your drinking water and carry it in stainless or glass, never microwave food in plastic, replace scratched plastic cutting boards and containers, check cosmetics for polymer ingredients like polyethylene and acrylates, and prefer natural fibers for clothing and bedding.',
      'The app helps with sequencing: it shows which of these matters most for your routine, then tracks whether the change actually moved your exposure index.',
    ],
  },
  {
    category: 'Privacy',
    question: 'How is my data used and protected?',
    answer: [
      'The app works with an anonymous account by default; you can use your 3 free scans without signing up. Data is encrypted in transit and at rest.',
      'Anonymized scan data may be used to improve scoring and to build aggregate exposure research. It is never sold. You can delete your account and data at any time from the app or at microplasticsapp.com/delete-account.',
    ],
  },
]
